import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import SharedTransferList from '../../../../shared/shared-transfer-list';
import { SignatureTemplateFormValues } from '../signature-templates-form-page';

import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import Alert from '@mui/material/Alert';

interface StepSignatureTemplateTargetsProps {
  control: Control<SignatureTemplateFormValues>;
  watch: UseFormWatch<SignatureTemplateFormValues>;
  setValue: UseFormSetValue<SignatureTemplateFormValues>;
  errors: FieldErrors<SignatureTemplateFormValues>;
  domains: string[];
  trigger: UseFormTrigger<SignatureTemplateFormValues>;
  clearErrors: UseFormClearErrors<SignatureTemplateFormValues>;
}

export default function StepSignatureTemplateTargets({
  control,
  watch,
  setValue,
  errors,
  domains,
  trigger,
  clearErrors,
}: StepSignatureTemplateTargetsProps) {
  const sources = watch('sources') || [];
  const sourceType = watch('sourceType');
  const allSource = watch('allSource');

  function addSelection(emails: string[]) {
    const uniqueEmails = Array.from(new Set([...sources!, ...emails]));
    setValue('sources', uniqueEmails, {
      shouldDirty: true,
    });
    clearErrors();
    if (uniqueEmails.length === 0) {
      trigger('sources');
    }
  }

  function removeSelection(emails: string[]) {
    const newList = sources!.filter((userEmail) => !emails.includes(userEmail));
    setValue('sources', newList, {
      shouldDirty: true,
    });
  }

  function handleAddAllChecked(value: boolean) {
    setValue('allSource', value);
    if (value === true) {
      setValue('sources', []);
      clearErrors();
    }
  }

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap={1} sx={{ flexShrink: 0, height: '100%' }}>
        <Stack
          flexDirection={'row'}
          gap={4}
          alignItems="center"
          justifyContent="center"
        >
          <Controller
            name={`sourceType`}
            control={control}
            render={({ field: props }) => {
              return (
                <FormControl>
                  <FormLabel>
                    {sources!.length > 0
                      ? 'Target Type (Only one target type is allowed)'
                      : 'Target Type'}
                  </FormLabel>
                  <RadioGroup
                    defaultValue="User"
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                      clearErrors();
                    }}
                    row
                  >
                    <FormControlLabel
                      value="User"
                      control={<Radio />}
                      label="User"
                      disabled={sources!.length > 0 || allSource === true}
                    />
                    <FormControlLabel
                      value="Group"
                      control={<Radio />}
                      label="Group"
                      disabled={sources!.length > 0 || allSource === true}
                    />
                    <FormControlLabel
                      value="OrgUnit"
                      control={<Radio />}
                      label="Org Unit"
                      disabled={sources!.length > 0 || allSource === true}
                    />
                    <FormControlLabel
                      value="Domain"
                      control={<Radio />}
                      label="Domain"
                      disabled={sources!.length > 0 || allSource === true}
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Stack>
        <Stack flexDirection={'row'} flex={1} minHeight={0}>
          <Controller
            name={'sources'}
            control={control}
            render={() => (
              <SharedTransferList
                sources={sources}
                addSelection={addSelection}
                removeSelection={removeSelection}
                handleAddAllChecked={handleAddAllChecked}
                errors={!!errors.sources}
                helperText={errors?.sources?.message || ' '}
                allSource={allSource}
                domains={domains || []}
                sourceType={sourceType}
                hasSelectAllOption={true}
              />
            )}
          />
        </Stack>
        <Stack sx={{ alignItems: 'center' }}>
          {sourceType === 'Group' && (
            <Grow in={true}>
              <Alert
                severity="warning"
                sx={{
                  '& .MuiAlert-icon': {
                    color: 'warning.main',
                  },
                  width: 'fit-content',
                }}
              >
                If the selected group(s) has the "All current and future users
                within domain", this user will not be processed.
              </Alert>
            </Grow>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
